import React, { useEffect } from 'react';
import styles from './QuickViewModal.module.css';
import Image from 'next/image';
import { normalizeImageUrl } from '@/utils/image';

interface QuickViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportData: any;
}

const QuickViewModal: React.FC<QuickViewModalProps> = ({ isOpen, onClose, reportData }) => {
    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function to restore scroll when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !reportData) return null;

    const { title, summary, metadata_json, pricing, published_at, blocks } = reportData;
    const coverImageUrl = metadata_json?.cover_image_url || metadata_json?.image_url;
    const price = pricing?.price_amount || 0;
    const discountedPrice = pricing?.discounted_price;
    const publishDate = new Date(published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
    });

    // Get first 2 sections from description block
    const descriptionBlock = blocks?.find((block: any) => block.block_type === 'description');
    const sections = descriptionBlock?.block_data_json?.sections?.slice(0, 2) || [];

    const renderSection = (section: any, index: number) => {
        if (section.type === 'bullets') {
            return (
                <div key={index} className={styles.section}>
                    <h3 className={styles.sectionHeading}>{section.heading}</h3>
                    <ul className={styles.bulletList}>
                        {section.content.map((item: string, i: number) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>
            );
        } else if (section.type === 'paragraph') {
            return (
                <div key={index} className={styles.section}>
                    <h3 className={styles.sectionHeading}>{section.heading}</h3>
                    <p className={styles.paragraph}>{section.content}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {/* Overlay */}
            <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} onClick={onClose} />

            {/* Modal */}
            <div className={`${styles.modal} ${isOpen ? styles.open : ''}`}>
                <div className={styles.modalScrollContainer}>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>

                    <div className={styles.modalContent}>
                        {/* Header Section: Title, Summary, Price - Centered */}
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>{title}</h1>
                            <p className={styles.summary}>{summary}</p>

                            {/* Static Note */}
                            <p className={styles.note}>
                                Note that the outlook for this market is being affected by rapid changes in trade relations and tariffs globally. The report will be updated prior to delivery to reflect the latest status, including revised forecasts and quantified impact analysis. The report's Recommendations and Conclusions sections will be updated to give strategies for entities dealing with the fast-moving international environment.
                            </p>

                            <div className={styles.priceSection}>
                                {discountedPrice ? (
                                    <>
                                        <span className={styles.price}>${discountedPrice.toFixed(2)}</span>
                                        <span className={styles.originalPrice}>${price.toFixed(2)}</span>
                                    </>
                                ) : (
                                    <span className={styles.price}>${price.toFixed(2)}</span>
                                )}
                                <span className={styles.publishDate}>{publishDate}</span>
                            </div>
                        </div>

                        {/* Split Body Section: Image (Left) | Content (Right) */}
                        <div className={styles.splitBody}>
                            <div className={styles.leftPanel}>
                                <div className={styles.imageWrapper}>
                                    {coverImageUrl ? (
                                        <Image
                                            src={normalizeImageUrl(coverImageUrl)}
                                            alt={title}
                                            width={300}
                                            height={400}
                                            className={styles.coverImage}
                                        />
                                    ) : (
                                        <div className={styles.placeholderImage}>
                                            <div className={styles.imageTop}>
                                                <div className={styles.imagePlaceholder}></div>
                                            </div>
                                            <div className={styles.imageBottom}>
                                                <div className={styles.imageText}>
                                                    {(title || '').split(' ')[0]}<br />
                                                    {(title || '').split(' ')[1] || ''}
                                                </div>
                                                <div className={styles.imageSubtext}>
                                                    Global Market Report
                                                </div>
                                            </div>
                                            <div className={styles.imageLogo}></div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.rightPanel}>
                                {sections.map((section: any, index: number) => renderSection(section, index))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default QuickViewModal;
